import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { APP_BASE_HREF, Location } from '@angular/common';
import { PreloadAllModules, provideRouter, withDebugTracing, withDisabledInitialNavigation, withPreloading } from '@angular/router';
import { appBaseHrefFactory, routes } from './app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DefaultOAuthInterceptor } from './shared/config/oauth.interceptor';
import { OAuthModule, OAuthModuleConfig, OAuthStorage } from 'angular-oauth2-oidc';
import { authModuleConfig } from './shared/config/oauth.module.config';
import { authConfig } from './shared/config/auth.config';
import { DefaultRequestHeaderInterceptor } from './shared/config/request-headers.interceptor';
import { LocalizeParser, LocalizeRouterSettings, withLocalizeRouter } from '@gilsdav/ngx-translate-router';
import { Language } from './shared/config/app.constants';
import { LocalizeRouterHttpLoader } from '@gilsdav/ngx-translate-router-http-loader';
import { provideStore, StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { RootEffects } from './root.effects';
import { AnalyticsResourceModule } from './resources/analytics';
import { RedirectionResourceModule } from './resources/redirection';
import { ReCaptchaV3Module } from 'src/lib/resources/recaptcha-v3';
import { httpConfig,reCaptchaConfigFactory } from './config';
import { HttpModule } from 'src/lib/http';

//Require of AOT Compilation
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

export const provideTranslation = () => ({
  defaultLanguage: Language.ENGLISH,
  loader: {
    provide: TranslateLoader,
    useFactory: HttpLoaderFactory,
    deps: [HttpClient],
  },
});

export function HttpLoaderRouterFactory(
  translate: TranslateService,
  location: Location,
  settings: LocalizeRouterSettings,
  http: HttpClient
) {
  settings.defaultLangFunction = currentLang;
  return new LocalizeRouterHttpLoader(translate, location, settings, http, 'assets/i18n/locales.json');
}

/**
 * Application specific logic to get current lang on init
 */
export function currentLang(): Language {
  const urlParams = new URLSearchParams(window.location.search);
  // Try to get the language from URL parameters
  const lang = urlParams.get('lang') || urlParams.get('langue');
  // If a valid language is found, return it; otherwise, check the browser's language
  if (lang) {
    return lang as Language;
  }
  // Fallback to browser language
  return navigator.language === Language.FRENCH ? Language.FRENCH : Language.ENGLISH;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    provideRouter(
      routes,
      withPreloading(PreloadAllModules),
      withDebugTracing(),
      withDisabledInitialNavigation(),
      withLocalizeRouter(routes, {
        parser: {
          provide: LocalizeParser,
          useFactory: HttpLoaderRouterFactory,
          deps: [TranslateService, Location, LocalizeRouterSettings, HttpClient]
        },
        alwaysSetPrefix: true,
        useCachedLang: true,
        initialNavigation: true
      })
    ),
    {
      provide: APP_BASE_HREF,
      useFactory: appBaseHrefFactory
    },

    {
      provide: OAuthStorage,
      useValue: localStorage
    },
    {
      provide: OAuthModule,
      useValue: authConfig
    },
    {
      provide: OAuthModuleConfig,
      useValue: authModuleConfig

    },
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DefaultOAuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DefaultRequestHeaderInterceptor,
      multi: true
    },
    importProvidersFrom(
      [
        OAuthModule.forRoot(authModuleConfig),
        TranslateModule.forRoot(provideTranslation()),
        EffectsModule.forRoot([RootEffects]),
        StoreModule.forRoot({}),
        ReCaptchaV3Module.forRoot(reCaptchaConfigFactory),
        AnalyticsResourceModule,
        RedirectionResourceModule,
        HttpModule.forRoot(httpConfig),

      ]
    ),



  ],
};
